// A company in the Insurance Directory edited together with everyone who works
// there. "Company" means a carrier or a general agency (a wholesaler a broker
// sends RFPs through): an RFP goes to EVERY sales contact of the one picked, so
// both want the same shape.
//
// The two kinds differ in exactly two ways:
//   GA      the broker owns the firm, so its name is typed and creates a row
//   carrier a shared row nobody here creates, so its name is fixed
//
// Three exports, because the same form appears in two places: the hook, the
// fields alone (used inside the shared "Add Contact" dialog when Type is GA),
// and the fields in their own dialog ("Edit contacts" on a group).
//
// Writes through the ordinary endpoints, one request per changed row. No batch
// endpoint: a company has a handful of contacts, not hundreds.

import { useEffect, useState } from "react";
import {
  Plus,
  Trash2,
  Loader2,
  AlertCircle,
  Check,
  ChevronsUpDown,
  X,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { formatPhoneInputValue } from "@/lib/phone";

export interface GeneralAgencyRef {
  id: number;
  name: string;
  isActive?: boolean | null;
  /** Carriers this GA can place business with. Empty means it can place none. */
  carrierIds?: number[];
}

interface CarrierOption {
  id: number;
  name: string;
  isActive?: boolean | null;
}

export interface CompanyMember {
  id: number;
  contactName?: string;
  email: string;
  phone?: string;
  contactType?: "sales" | "service";
}

/** Which company the rows belong to. A GA may be null, meaning "create one". */
export type CompanyTarget =
  | { kind: "ga"; generalAgency: GeneralAgencyRef | null }
  | { kind: "carrier"; carrier: { id: number; name: string } };

interface ContactRow {
  /** Absent on a row the broker just added — that is how we know to POST it. */
  id?: number;
  contactName: string;
  email: string;
  phone: string;
  contactType: "sales" | "service";
}

// Same shape the contact form uses. A contact with no working address is dead
// weight: the RFP picks its recipients by email.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const blankRow = (): ContactRow => ({
  contactName: "",
  email: "",
  phone: "",
  contactType: "sales",
});

const isBlank = (row: ContactRow) =>
  !row.contactName.trim() && !row.email.trim() && !row.phone.trim();

const targetId = (target: CompanyTarget) =>
  target.kind === "ga" ? target.generalAgency?.id : target.carrier.id;

const targetName = (target: CompanyTarget) =>
  target.kind === "ga" ? (target.generalAgency?.name ?? "") : target.carrier.name;

export function useCompanyContactsForm({
  target,
  members,
  allContacts,
  active,
  onSaved,
}: {
  target: CompanyTarget;
  /** Every directory contact already linked to this company. */
  members: CompanyMember[];
  /**
   * Every contact of the same kind, whatever company. Only used to avoid
   * creating a second copy of someone: naming a GA that already exists joins it
   * (the server dedupes by name), and its people are already on file.
   */
  allContacts: Array<CompanyMember & { carrierId?: number | null; generalAgencyId?: number | null }>;
  /** True while the form is on screen. Reloads from props on each open. */
  active: boolean;
  onSaved: () => void;
}) {
  const [name, setName] = useState("");
  const [rows, setRows] = useState<ContactRow[]>([blankRow()]);
  const [carrierIds, setCarrierIds] = useState<number[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Shares React Query's cache with the Directory page, so this costs nothing.
  const { data: carrierOptions = [] } = useQuery<CarrierOption[]>({
    queryKey: ["/api/carriers"],
    enabled: target.kind === "ga",
  });

  // A carrier's name belongs to the shared carriers table, never edited here.
  const nameIsEditable = target.kind === "ga";

  // Reload on each appearance, so a cancelled edit leaves nothing behind.
  useEffect(() => {
    if (!active) return;
    setErrors([]);
    setName(targetName(target));
    setCarrierIds(target.kind === "ga" ? (target.generalAgency?.carrierIds ?? []) : []);
    setRows(
      members.length
        ? members.map((member) => ({
            id: member.id,
            contactName: member.contactName ?? "",
            email: member.email ?? "",
            phone: member.phone ?? "",
            contactType: member.contactType === "service" ? "service" : "sales",
          }))
        : [blankRow()],
    );
    // Only on appear / company switch: a background refetch of the contact list
    // must not wipe what is being typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, target.kind, targetId(target)]);

  const updateRow = (index: number, patch: Partial<ContactRow>) =>
    setRows((current) => current.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  const addRow = () => setRows((current) => [...current, blankRow()]);

  const removeRow = (index: number) => {
    const row = rows[index];
    // Confirm only for someone who exists; a row just added is theirs to undo.
    if (row.id && !confirm(`Remove ${row.contactName || row.email} from ${name}?`)) return;
    setRows((current) => {
      const next = current.filter((_, i) => i !== index);
      return next.length ? next : [blankRow()];
    });
  };

  const filledRows = rows.filter((row) => !isBlank(row));
  const hasSendableContact = filledRows.some(
    (row) => row.contactType === "sales" && row.email.trim(),
  );

  const save = useMutation({
    mutationFn: async () => {
      const failures: string[] = [];
      const trimmedName = name.trim();
      const linkField = target.kind === "ga" ? "generalAgencyId" : "carrierId";

      // 1. The company. Only a GA can be created or renamed here; POST returns
      //    the existing row when the name is taken, so a repeat name joins.
      let companyId = targetId(target);
      if (target.kind === "ga") {
        // One dialog holds both, so one request saves both.
        if (!companyId) {
          const created = await apiRequest("POST", "/api/general-agencies", {
            name: trimmedName,
            carrierIds,
          });
          if (!created.ok) throw new Error("Could not save the general agency");
          companyId = ((await created.json()) as GeneralAgencyRef).id;
        } else {
          const saved = await apiRequest("PUT", `/api/general-agencies/${companyId}`, {
            name: trimmedName,
            carrierIds,
          });
          if (!saved.ok) throw new Error("Could not save the general agency");
        }
      }

      // 2. People whose row the broker took out.
      const keptIds = new Set(filledRows.map((row) => row.id).filter(Boolean));
      for (const member of members) {
        if (keptIds.has(member.id)) continue;
        const removed = await apiRequest("DELETE", `/api/directory-contacts/${member.id}`);
        if (!removed.ok) failures.push(member.contactName || member.email);
      }

      // 3. The rest. On a directory contact `name` is the COMPANY and NOT NULL,
      //    so every row carries it, and follows a GA rename.
      for (const row of filledRows) {
        const payload = {
          name: trimmedName,
          contactName: row.contactName.trim(),
          email: row.email.trim(),
          phone: row.phone.trim(),
          contactType: row.contactType,
          type: target.kind === "ga" ? "ga" : "carrier",
          [linkField]: companyId,
        };

        // A new row can still be somebody already on file, because naming an
        // existing GA joins it and brings its people. Match on email, or they
        // get added twice.
        const existingId =
          row.id ??
          allContacts.find(
            (contact) =>
              contact[linkField] === companyId &&
              contact.email?.toLowerCase() === row.email.trim().toLowerCase(),
          )?.id;

        const response = existingId
          ? await apiRequest("PUT", `/api/directory-contacts/${existingId}`, payload)
          : await apiRequest("POST", "/api/directory-contacts", payload);
        // apiRequest resolves on 4xx/5xx: unchecked, a failure looks like a save.
        if (!response.ok) failures.push(row.contactName || row.email);
      }

      if (failures.length) throw new Error(`Could not save: ${failures.join(", ")}`);
      return trimmedName;
    },
    onSuccess: (savedName) => {
      queryClient.invalidateQueries({ queryKey: ["/api/directory-contacts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/general-agencies"] });
      toast({ description: `${savedName} saved.` });
      onSaved();
    },
    onError: (error: Error) => setErrors([error.message]),
  });

  const submit = () => {
    const problems: string[] = [];
    // Only a GA's name can be blank: a carrier's comes from the shared row.
    if (nameIsEditable && !name.trim()) problems.push("Give the general agency a name.");
    // No carriers means no RFP can go through this GA at all: an error, unlike
    // the soft warning a missing sales contact gets.
    if (target.kind === "ga" && !carrierIds.length) {
      problems.push("Pick at least one carrier this GA can quote.");
    }
    if (!filledRows.length) problems.push("Add at least one contact.");
    for (const row of filledRows) {
      const who = row.contactName || "A contact";
      if (!row.email.trim()) {
        problems.push(`${who} needs an email address.`);
      } else if (!EMAIL_PATTERN.test(row.email.trim())) {
        problems.push(`${who} has an email address that does not look right.`);
      }
    }
    setErrors(problems);
    if (problems.length) return;
    save.mutate();
  };

  const toggleCarrier = (carrierId: number) =>
    setCarrierIds((current) =>
      current.includes(carrierId)
        ? current.filter((id) => id !== carrierId)
        : [...current, carrierId],
    );

  return {
    kind: target.kind,
    name,
    setName,
    nameIsEditable,
    carrierIds,
    toggleCarrier,
    carrierOptions: carrierOptions.filter((carrier) => carrier.isActive !== false),
    rows,
    updateRow,
    addRow,
    removeRow,
    errors,
    hasSendableContact,
    isSaving: save.isPending,
    submit,
  };
}

export type CompanyContactsForm = ReturnType<typeof useCompanyContactsForm>;

// A GA is only asked for the carriers it holds an appointment with, so this
// list is what an RFP through it can contain.
function CarrierPicker({ form }: { form: CompanyContactsForm }) {
  const [open, setOpen] = useState(false);
  // Pick order is meaningless; these are read as a list of who can quote.
  const selected = form.carrierOptions
    .filter((carrier) => form.carrierIds.includes(carrier.id))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="space-y-2">
      <Label>
        Carriers this GA works with <span className="text-red-500">*</span>
      </Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal"
          >
            {form.carrierIds.length
              ? `${form.carrierIds.length} of ${form.carrierOptions.length} carriers`
              : "Pick the carriers this GA can quote"}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[--radix-popover-trigger-width] p-0"
          onWheel={(event) => event.stopPropagation()}
        >
          <Command>
            <CommandInput placeholder="Search carriers..." />
            <CommandEmpty>No carrier found.</CommandEmpty>
            <CommandList style={{ maxHeight: "250px", overflowY: "auto" }}>
              <CommandGroup>
                {form.carrierOptions.map((carrier) => {
                  const isPicked = form.carrierIds.includes(carrier.id);
                  return (
                    <CommandItem
                      key={carrier.id}
                      value={carrier.name}
                      // Stays open: this is a multi-select, not a picker.
                      onSelect={() => form.toggleCarrier(carrier.id)}
                    >
                      <Check
                        className={cn("mr-2 h-4 w-4", isPicked ? "opacity-100" : "opacity-0")}
                      />
                      {carrier.name}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {selected.length > 0 ? (
        <div className="rounded-md border bg-muted/30 p-2.5">
          <div className="flex flex-wrap gap-1.5">
            {selected.map((carrier) => (
              <Badge
                key={carrier.id}
                variant="outline"
                // The platform's selection tint, as on a ticked card in step 4.
                className="gap-1.5 py-1 pl-2.5 pr-1 text-xs font-medium border-primary/30 bg-primary/10 text-primary"
              >
                {carrier.name}
                <button
                  type="button"
                  aria-label={`Remove ${carrier.name}`}
                  onClick={() => form.toggleCarrier(carrier.id)}
                  className="rounded-sm p-0.5 hover:bg-primary/20"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        </div>
      ) : (
        // Saving is blocked in this state, so amber, not a grey hint.
        <p className="flex items-start gap-1.5 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-400">
          <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-px" />
          No carriers picked, so no RFP can go through this GA. Pick the ones it can quote.
        </p>
      )}
    </div>
  );
}

export function CompanyContactsFields({ form }: { form: CompanyContactsForm }) {
  return (
    <div className="space-y-4">
      {form.nameIsEditable && (
        <div className="space-y-2">
          <Label htmlFor="company-contacts-name">
            General agency name <span className="text-red-500">*</span>
          </Label>
          <Input
            id="company-contacts-name"
            value={form.name}
            onChange={(event) => form.setName(event.target.value)}
            placeholder="e.g. Brewer GA Group"
          />
        </div>
      )}

      {form.kind === "ga" && <CarrierPicker form={form} />}

      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Label>Contacts</Label>
          <span className="text-xs text-muted-foreground">
            Sales contacts receive the RFP. Service contacts are for reference.
          </span>
        </div>

        <div className="space-y-2">
          {form.rows.map((row, index) => (
            <div
              key={row.id ?? `new-${index}`}
              className="grid grid-cols-1 sm:grid-cols-[1.2fr_1.5fr_1fr_auto_auto] gap-2 items-center"
            >
              <Input
                value={row.contactName}
                onChange={(event) => form.updateRow(index, { contactName: event.target.value })}
                placeholder="Contact name"
              />
              <Input
                type="email"
                value={row.email}
                onChange={(event) => form.updateRow(index, { email: event.target.value })}
                placeholder="Email"
              />
              <Input
                value={row.phone}
                onChange={(event) =>
                  form.updateRow(index, { phone: formatPhoneInputValue(event.target.value) })
                }
                placeholder="Phone"
              />
              <Select
                value={row.contactType}
                onValueChange={(value) =>
                  form.updateRow(index, { contactType: value as "sales" | "service" })
                }
              >
                <SelectTrigger className="w-[110px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sales">Sales</SelectItem>
                  <SelectItem value="service">Service</SelectItem>
                </SelectContent>
              </Select>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => form.removeRow(index)}
                aria-label="Remove contact"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>

        <Button type="button" variant="outline" size="sm" onClick={form.addRow}>
          <Plus className="mr-2 h-4 w-4" />
          Add another contact
        </Button>
      </div>

      {/* A warning, not a block. Step 4 greys out a GA in this state, so say
          now why it would not be pickable. */}
      {!form.hasSendableContact && (
        <p className="text-xs flex items-start gap-1.5 text-amber-700 dark:text-amber-500">
          <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          {form.kind === "ga"
            ? "With no sales contact you can still save this, but you will not be able to send an RFP to it."
            : "With no sales contact this carrier gets left out of any RFP it is picked for."}
        </p>
      )}

      {form.errors.length > 0 && (
        <div className="text-sm text-red-600 dark:text-red-400 space-y-1">
          {form.errors.map((error) => (
            <p key={error}>{error}</p>
          ))}
        </div>
      )}
    </div>
  );
}

export function CompanyContactsDialog({
  open,
  onOpenChange,
  target,
  members,
  allContacts,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: CompanyTarget | null;
  members: CompanyMember[];
  allContacts: Array<CompanyMember & { carrierId?: number | null; generalAgencyId?: number | null }>;
}) {
  // Nothing to edit until a company is chosen, and the hook must not be called
  // conditionally, so stand in a blank GA rather than returning early.
  const safeTarget: CompanyTarget = target ?? { kind: "ga", generalAgency: null };
  const form = useCompanyContactsForm({
    target: safeTarget,
    members,
    allContacts,
    active: open && !!target,
    onSaved: () => onOpenChange(false),
  });

  const existingName = targetName(safeTarget);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {existingName ? `Edit ${existingName}` : "Add General Agency"}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            An RFP goes to every sales contact listed here.
          </p>
        </DialogHeader>

        <CompanyContactsFields form={form} />

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={form.submit} disabled={form.isSaving}>
            {form.isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
